import React, { useState } from 'react'
import {
  normalizePhoneNumber,
  formatDate,
  formatPhone
} from '@@partner:helpers/util'
import { useFetchSingleCustomer } from '@@partner:graphql/query/allCustomers/singleCustomer.gql'
import { useSbpBanks } from '@@partner:helpers/sbp'

import { Col, Grid, Row, Panel, Tag, Avatar, Tabs, Badge, Rate, Button, IconButton } from 'rsuite'
import CreditCardPlusIcon from '@rsuite/icons/CreditCardPlus'
import ReviewIcon from '@rsuite/icons/Review'
import CouponIcon from '@rsuite/icons/Coupon'
import ListIcon from '@rsuite/icons/List'
import EditIcon from '@rsuite/icons/Edit'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import CustomerStatusLabel from './common/CustomerStatusLabel'
import CustomerCardTypeLabel from './common/CustomerCardTypeLabel'
import CustomerCardCountryLabel from './common/CustomerCardCountryLabel'
import CustomerSaleInfo from './common/sale/CustomerSaleInfo'
import LegacyPhoneIcon from '@rsuite/icons/legacy/Phone'
import CustomerOrders from './CustomerOrders'
import CustomerEditDrawer from './CustomerEditDrawer'

import './ViewCustomer.scss'
import CustomerFeedback from '@components/order/feedback/CustomerFeedback'

export interface ViewCustomerProps {
  customerId: number
}

export default function ViewCustomer ({ customerId }: ViewCustomerProps) {
  const [ isEditDrawerOpen, setIsEditDrawerOpen ] = useState(false)
  
  const { data, loading, error, refetch } = useFetchSingleCustomer({
    id: customerId
  })

  console.log(data)
  const { banks: sbpBanks } = useSbpBanks()

  const customer = data?.customer

  // Разделяем токены на карты и СБП (sbp определяется по полю card)
  const cardTokens = customer?.paymentTokens.filter(token => token.info.card && token.info.card !== 'sbp') ?? []
  const sbpTokens = customer?.paymentTokens.filter(token => token.info.card === 'sbp') ?? []
  const allPaymentTokens = customer?.paymentTokens ?? []

  if (loading) {
    return <AppLoader.RowsWithText content="Загружаем заказ" />
  }

  if (error) {
    return <GraphQLCommonErrorDisplay error={error} />
  }

  if (!customer) {
    return <p>Customer not found!</p>
  }

  // Статистика клиента
  const feedbacksCount = customer?.feedbacks?.length ?? 0
  const averageRating = feedbacksCount > 0
    ? customer.feedbacks.reduce((sum, f) => sum + (f.general_rating || 0), 0) / feedbacksCount
    : 0

  return (
    <div className="ViewCustomer">
      {/* Шапка с аватаром и основной информацией */}
      <Panel shaded bordered className="mb-4">
        <div className="flex items-start gap-6">
          <Avatar
            circle
            size="lg"
            className="bg-blue-500 text-white flex-shrink-0"
            style={{ width: 80, height: 80, fontSize: 32 }}
          >
            {customer?.first_name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-xl font-semibold m-0">{customer?.fullName}</h2>
              <CustomerStatusLabel status={customer?.status} />
              <IconButton
                icon={<EditIcon />}
                appearance="subtle"
                size="sm"
                onClick={() => setIsEditDrawerOpen(true)}
              >
                Редактировать
              </IconButton>
            </div>
            <div className="flex flex-wrap gap-4 text-gray-600">
              {customer?.phone && (
                <a 
                  href={`tel:${normalizePhoneNumber(customer?.phone)}`}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  <LegacyPhoneIcon /> {formatPhone(customer?.phone)}
                </a>
              )}
              {customer?.receipt_email && (
                <span>{customer?.receipt_email}</span>
              )}
            </div>
            <div className="flex gap-4 mt-3 text-sm text-gray-500">
              <span>Создан: {formatDate(new Date(customer.created_at))}</span>
              <span>Обновлен: {formatDate(new Date(customer?.updated_at))}</span>
            </div>
            
            {/* Статистика */}
            <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{customer?.ordersCount ?? 0}</div>
                <div className="text-xs text-gray-500">Заказов</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-500">{feedbacksCount}</div>
                <div className="text-xs text-gray-500">Отзывов</div>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-bold text-green-600">
                    {averageRating > 0 ? averageRating.toFixed(1) : '—'}
                  </span>
                  {averageRating > 0 && <Rate value={1} max={1} readOnly size="xs" color="yellow" />}
                </div>
                <div className="text-xs text-gray-500">Средняя оценка</div>
              </div>
            </div>
          </div>
        </div>
      </Panel>

      {/* Вкладки с данными */}
      <Panel bordered className="mb-4">
        <Tabs defaultActiveKey="orders" appearance="subtle">
          <Tabs.Tab 
            eventKey="orders" 
            title={
              <span className="flex items-center gap-2">
                <ListIcon />
                Заказы
              </span>
            }
          >
            <div className="pt-4">
              <CustomerOrders customerId={customerId} />
            </div>
          </Tabs.Tab>

          <Tabs.Tab 
            eventKey="payments" 
            title={
              <span className="flex items-center gap-2">
                <CreditCardPlusIcon />
                Способы оплаты
                {allPaymentTokens.length > 0 && (
                  <Badge content={allPaymentTokens.length} />
                )}
              </span>
            }
          >
            <div className="pt-4">
              {allPaymentTokens.length > 0 ? (
                <>
                  {/* Банковские карты */}
                  {cardTokens.length > 0 && (
                    <>
                      <h4 className="text-gray-600 mb-3 font-medium">Банковские карты</h4>
                      <Grid fluid>
                        <Row gutter={16}>
                          {cardTokens.map((paymentToken, index) => {
                            const paymentInfo = paymentToken.info || {}
                            return (
                              <Col xs={24} md={8} lg={6} key={`card-${index}`} className="mb-4">
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-medium uppercase">{paymentInfo.card || 'Карта'}</span>
                                    {paymentInfo?.type && (
                                      <CustomerCardTypeLabel type={paymentInfo.type} />
                                    )}
                                  </div>
                                  <div className="text-gray-600 font-mono text-lg">
                                    •••• •••• •••• {paymentInfo?.last || '****'}
                                  </div>
                                  {paymentInfo?.country && (
                                    <div className="text-sm text-gray-500 mt-2">
                                      <CustomerCardCountryLabel country={paymentInfo.country} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )
                          })}
                        </Row>
                      </Grid>
                    </>
                  )}

                  {/* СБП */}
                  {sbpTokens.length > 0 && (
                    <>
                      <h4 className="text-gray-600 mb-3 mt-4 font-medium flex items-center gap-2">
                        <img 
                          src="https://qr.nspk.ru/proxyapp/logo/sbp-logo.png" 
                          alt="СБП" 
                          className="h-5"
                          onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                        Система быстрых платежей (СБП)
                      </h4>
                      <Grid fluid>
                        <Row gutter={16}>
                          {sbpTokens.map((paymentToken, index) => {
                            // last содержит ID банка
                            const bankId = paymentToken.info?.last
                            const sbpBank = sbpBanks.find(b => b.bankName === bankId || b.schema === bankId)
                            
                            return (
                              <Col xs={24} md={8} lg={6} key={`sbp-${index}`} className="mb-4">
                                <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                                  <div className="flex items-center gap-3">
                                    {sbpBank?.logoURL ? (
                                      <img 
                                        src={sbpBank.logoURL} 
                                        alt={sbpBank.bankName}
                                        className="w-10 h-10 rounded-lg object-contain bg-white p-1"
                                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                                      />
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                        <span className="text-purple-600 font-bold text-xs">СБП</span>
                                      </div>
                                    )}
                                    <div>
                                      <div className="font-medium text-gray-800">
                                        {sbpBank?.bankName || 'СБП'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Привязан {new Date(paymentToken.created_at).toLocaleDateString('ru-RU')}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )
                          })}
                        </Row>
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                <div className="text-gray-400 text-center py-8">Нет сохраненных способов оплаты</div>
              )}
            </div>
          </Tabs.Tab>

          <Tabs.Tab 
            eventKey="feedbacks" 
            title={
              <span className="flex items-center gap-2">
                <ReviewIcon />
                Отзывы
                {customer.feedbacks.length > 0 && (
                  <Badge content={customer.feedbacks.length} />
                )}
              </span>
            }
          >
            <div className="pt-4">
              {customer.feedbacks.length > 0 ? (
                <Grid fluid>
                  <Row gutter={16}>
                    {customer.feedbacks.map(feedback => (
                      <Col xs={24} md={12} lg={8} key={feedback.id} className="mb-4">
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <Tag color="blue">Заказ #{feedback.order_id}</Tag>
                            {feedback.need_call && (
                              <Tag color="orange">Нужен звонок</Tag>
                            )}
                          </div>
                          
                          <div className="mb-3">
                            <Rate value={feedback.general_rating} readOnly size="xs" color="yellow" />
                          </div>
                          
                          {feedback.review && (
                            <div className="text-gray-700 text-sm mb-3 line-clamp-3">
                              {feedback.review}
                            </div>
                          )}
                          
                          {feedback.images && feedback.images.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {feedback.images.slice(0, 3).map((img, idx) => (
                                <img 
                                  key={idx}
                                  src={img.url} 
                                  alt={`Фото ${idx + 1}`}
                                  className="w-16 h-16 object-cover rounded border"
                                />
                              ))}
                              {feedback.images.length > 3 && (
                                <div className="w-16 h-16 rounded border bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                  +{feedback.images.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Grid>
              ) : (
                <div className="text-gray-400 text-center py-8">Нет отзывов</div>
              )}
            </div>
          </Tabs.Tab>

          <Tabs.Tab 
            eventKey="discounts" 
            title={
              <span className="flex items-center gap-2">
                <CouponIcon />
                Скидки
                {customer.usedPromocodes && customer.usedPromocodes.length > 0 && (
                  <Badge content={customer.usedPromocodes.length} />
                )}
              </span>
            }
          >
            <div className="pt-4">
              {customer.usedPromocodes && customer.usedPromocodes.length > 0 ? (
                <Grid fluid>
                  <Row gutter={16}>
                    {customer.usedPromocodes.map((sale) => (
                      <Col xs={24} md={12} lg={8} key={sale.id} className="mb-4">
                        <CustomerSaleInfo orderSale={sale} />
                      </Col>
                    ))}
                  </Row>
                </Grid>
              ) : (
                <div className="text-gray-400 text-center py-8">Нет использованных скидок</div>
              )}
            </div>
          </Tabs.Tab>
        </Tabs>
      </Panel>

      {/* Drawer редактирования клиента */}
      <CustomerEditDrawer
        isOpen={isEditDrawerOpen}
        customerId={customerId}
        onClose={() => setIsEditDrawerOpen(false)}
        onSaved={() => refetch()}
      />
    </div>
  )
}
